import type { Filters } from './types';

export const NEUTRAL_FILTERS: Filters = {
  brightness: 100,
  contrast: 100,
  saturation: 100,
  warmth: 0,
  grayscale: 0,
  blur: 0,
  vignette: 0,
};

export function isNeutral(f: Filters) {
  return (
    f.brightness === 100 &&
    f.contrast === 100 &&
    f.saturation === 100 &&
    f.warmth === 0 &&
    f.grayscale === 0 &&
    f.blur === 0 &&
    f.vignette === 0
  );
}

/**
 * Serialise filters to a CSS filter string.
 *
 * The DOM preview assigns this to `style.filter`, the exporter assigns the very
 * same string to `ctx.filter`. `scale` converts blur from base-canvas px into
 * whichever pixel space is being drawn.
 */
export function filtersToCSS(f: Filters, scale = 1): string {
  const parts: string[] = [];
  if (f.brightness !== 100) parts.push(`brightness(${f.brightness}%)`);
  if (f.contrast !== 100) parts.push(`contrast(${f.contrast}%)`);
  if (f.saturation !== 100) parts.push(`saturate(${f.saturation}%)`);
  if (f.warmth > 0) parts.push(`sepia(${f.warmth}%)`);
  if (f.grayscale > 0) parts.push(`grayscale(${f.grayscale}%)`);
  if (f.blur > 0) parts.push(`blur(${(f.blur * scale).toFixed(2)}px)`);
  return parts.length ? parts.join(' ') : 'none';
}

export interface FilterPreset {
  id: string;
  name: string;
  values: Partial<Filters>;
}

export const FILTER_PRESETS: FilterPreset[] = [
  { id: 'original', name: 'Original', values: {} },
  { id: 'mono', name: 'Mono', values: { grayscale: 100, contrast: 108 } },
  { id: 'silvertone', name: 'Silver', values: { grayscale: 100, brightness: 106, contrast: 94 } },
  { id: 'noir', name: 'Noir', values: { grayscale: 100, contrast: 132, brightness: 94 } },
  { id: 'vivid', name: 'Vivid', values: { saturation: 138, contrast: 110 } },
  { id: 'dramatic', name: 'Dramatic', values: { contrast: 128, saturation: 88, brightness: 96 } },
  { id: 'warm', name: 'Warm', values: { warmth: 32, saturation: 112, brightness: 104 } },
  { id: 'faded', name: 'Faded', values: { contrast: 84, saturation: 78, brightness: 108 } },
  { id: 'vintage', name: 'Vintage', values: { warmth: 55, contrast: 92, saturation: 86, vignette: 30 } },
];

export function applyPreset(base: Filters, preset: FilterPreset): Filters {
  return { ...NEUTRAL_FILTERS, blur: base.blur, ...preset.values };
}
